import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
// import { NgbTabset, NgbTabsetConfig } from '@ng-bootstrap/ng-bootstrap';
import { NgbNav, NgbNavConfig} from '@ng-bootstrap/ng-bootstrap';

import { ColumnChooserService, GridComponent, PageService, ToolbarService, VirtualScrollService, SortService, FilterService, SearchService } from '@syncfusion/ej2-angular-grids';
import { ClickEventArgs } from '@syncfusion/ej2-angular-navigations/src';
import { TooltipComponent } from '@syncfusion/ej2-angular-popups';
import { ToastrService } from 'ngx-toastr';
import { fadeInOut } from '../../shared/helpers/animations';
import { ICompanyContactStatus, ICompanyDetail, IDashboardRequest, IPaginatedResult, createDefaultDashboardRequest } from '../../shared/models/index';
import { AuthorizationService, CompanyContactService, StorageManagerService } from '../../shared/services/index';
import { DomSanitizer } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { DeleteButtonComponent } from '../common/delete-button/delete-button.component';
import { Utilities } from '../../shared/services/utilities';
@Component({
    selector: 'app-company-dashboard',
    templateUrl: './company-dashboard.component.html',
    styleUrls: ['./company-dashboard.component.scss'],
    providers: [ToolbarService, ColumnChooserService, PageService, VirtualScrollService, SortService, FilterService, SearchService],
    animations: [fadeInOut],
    standalone: false
})

export class CompanyDashboardComponent implements OnInit {
    @ViewChild('company_grid')
    public company_grid: GridComponent;
    @ViewChild('drafts_grid')
    public draftsGrid: GridComponent;
    @ViewChild('ngbNav', { static: true })
    public tabset: NgbNav;
    @ViewChild('tooltip')
    public tooltip: TooltipComponent;
    @ViewChild('deleteCompany')
    public deleteButton: DeleteButtonComponent;
    @ViewChild('deleteDraftCompany')
    public deleteButtonDrafts: DeleteButtonComponent;

    // Initialize with empty server-side format for Syncfusion grid
    public company_grid_data: any = { result: [], count: 0 };
    public drafts_grid_data: any = { result: [], count: 0 };
    public initialPage: Object;
    // Separate page settings for each grid to track server-side totals
    public companyPageSettings: Object;
    public draftsPageSettings: Object;
    public toolbarOptions: Object;
    errorMessage: string;
    count_company: number;
    count_draftcompany: number;
    FORM_STATUS_ACTIVE = 'A';
    FORM_STATUS_DRAFT = 'D';
    companyStatus: ICompanyContactStatus;
    loading = false;
    loadingDrafts = false;
    private isFiltering = false;
    public formatOptions: any;
    activeTab: string;
    sub: Subscription;
    public clipmodecolumn = { class: 'e-tooltipcolumn' };
    hasWritePermission = false;
    hasDeletePermission = false;
    selectedCompanyRevisionId: number | undefined;

    // Server-side pagination state for companies
    companyRequest: IDashboardRequest;
    // Server-side pagination state for drafts
    draftsRequest: IDashboardRequest;
    // Debounce timer for search
    private searchDebounceTimer: any;

    /**
     * Explicit selection persistence across paging/sorting/filtering.
     * Syncfusion persistSelection can still drop selection depending on templates/checkbox visibility,
     * so we keep the selected primary key and re-apply it on actionComplete.
     */
    private selectedCompanyRevisionIds = {
        company: null as number | null,
        drafts: null as number | null
    };

    private readonly dateFilterKeys: { [key: string]: { from: string; to: string } } = {
      modified: { from: 'modifiedFrom', to: 'modifiedTo' }
    };

  constructor(private router: Router, private toastr: ToastrService, private companySvc: CompanyContactService, private sanitizer: DomSanitizer,
              private storageManagerService: StorageManagerService, private authorizationService: AuthorizationService, ) {
  }

    ngOnInit(): void {
        this.checkUserPermission();

        this.toolbarOptions = this.createCustomToolbarItems(this.hasDeletePermission, this.hasWritePermission);

        this.initialPage = { currentPage: 1, pageSize: 25, pageSizes: [25, 50, 75, 100] };
        // Initialize separate page settings for each grid
        this.companyPageSettings = { currentPage: 1, pageSize: 25, pageSizes: [25, 50, 75, 100] };
        this.draftsPageSettings = { currentPage: 1, pageSize: 25, pageSizes: [25, 50, 75, 100] };
        this.formatOptions = { type: 'dateTime', format: Utilities.getLocaleDateString() + ' hh:mm a' };

        // Initialize server-side pagination requests with default sort by modified date descending
        this.companyRequest = createDefaultDashboardRequest(false);
        this.companyRequest.pageSize = 25;
        this.companyRequest.sortColumn = 'modified';
        this.companyRequest.sortDirection = 'DESC';
        this.draftsRequest = createDefaultDashboardRequest(true);
        this.draftsRequest.pageSize = 25;
        this.draftsRequest.sortColumn = 'modified';
        this.draftsRequest.sortDirection = 'DESC';

        // clear out previously saved info
        sessionStorage['currentCompanyId'] = null;

        // set focus on the invisible div so the first tab will bring it to the "Skip to Main Navigation" button because it must be the first tab target on the page (508 Compliance)
        this.initTabIndex();

        // Load both companies and drafts with server-side pagination
        this.loadCompanies();
        this.loadDrafts();

        this.activeTab = 'company';

        // Fix for footer spacing issues
        document.getElementsByTagName('footer')[0].classList.add("footer-spacing-fix");
    }

    initTabIndex() {
      (document.activeElement as HTMLElement).blur();

      const main = document.getElementById("firstDiv");

      if (main) {
        main.focus();
      }
    }

  checkUserPermission() {
    if (this.authorizationService.hasPermission('SSDP.CanWriteCompany')) {
      this.hasWritePermission = true;
    }
    if (this.authorizationService.hasPermission('SSDP.CanDeleteCompany')) {
      this.hasDeletePermission = true;
    }
  }

  tabChanged(event) {
    this.activeTab = event.nextId;
    this.selectedCompanyRevisionId = undefined;

    // restore selection when switching tabs
    setTimeout(() => this.restoreSelectionForActiveTab());
  }

  createCustomToolbarItems(deletePermission: boolean, writePermission: boolean) {
    let toolbarItems = [];

    if (writePermission) {
      toolbarItems.push(
          {
            text: 'Create New Company',
            tooltipText: 'Add a new company to this list',
            prefixIcon: 'fas fa-plus',
            id: 'create-form-btn'
          }
      );
    }

    if(deletePermission){
        toolbarItems.push(
            {
                text: 'Delete Company',
                tooltipText: 'Delete the selected company',
                prefixIcon: 'fas fa-trash',
                id: 'tool-bar-delete'
            }
        )
    }

    toolbarItems.push('Search');

    return toolbarItems;
  }

  tooltipValue(args) {

    let grid;

    if (args.target.scrollWidth <= args.target.clientWidth) {
      args.cancel = true;
    } else {

      switch (this.activeTab) {
        case 'drafts':
          grid = this.draftsGrid;
          break;
        case 'company':
          grid = this.company_grid;
          break;
      }

      const rowInfo = grid.getRowInfo(args.target);
      const rowData = rowInfo.rowData;
      this.tooltip.cssClass = 'customTooltip';
      this.tooltip.content = rowData[(rowInfo.column as any).field];
    }
  }

    // Grid toolbar click event handler
    toolbarClick(args: ClickEventArgs): void {
        if (args.item.id === 'create-form-btn') {
            this.router.navigate(['company-details']);
        }
        if (args.item.id === 'tool-bar-delete' && this.selectedCompanyRevisionId) {
            if(this.company_grid){
                this.deleteButton.revisionId = this.selectedCompanyRevisionId.toString();
                this.deleteButton.openDeleteModal();
            }
            else if(this.draftsGrid){
                this.deleteButtonDrafts.revisionId = this.selectedCompanyRevisionId.toString();
                this.deleteButtonDrafts.openDeleteModal();
            }
        }
    }

  // Load companies with server-side pagination
  loadCompanies() {
    // Skip if already loading to prevent duplicate requests
    if (this.loading) {
      return;
    }
    // Don't show custom loading overlay when filtering (Syncfusion shows its own spinner)
    if (!this.isFiltering) {
      this.loading = true;
    }
    this.companySvc.GetCompanyDashboardPaginated(this.companyRequest).subscribe(
      (result: IPaginatedResult<ICompanyDetail>) => {
        this.loading = false;
        this.isFiltering = false;

        // Ensure result has required properties with safe defaults
        const pageNumber = result?.pageNumber ?? 1;
        const pageSize = result?.pageSize ?? 25;
        const totalRecords = result?.totalRecords ?? 0;
        const data = result?.data ?? [];

        this.count_company = totalRecords;

        this.company_grid_data = { result: data, count: totalRecords };
      },
      error => {
        this.loading = false;
        this.isFiltering = false;
        this.toastr.error(error.message || 'Failed to load companies');
      }
    );
  }

  // Load drafts with server-side pagination
  loadDrafts() {
    // Skip if already loading to prevent duplicate requests
    if (this.loadingDrafts) {
      return;
    }
    // Don't show custom loading overlay when filtering (Syncfusion shows its own spinner)
    if (!this.isFiltering) {
      this.loadingDrafts = true;
    }
    this.companySvc.GetCompanyDashboardPaginated(this.draftsRequest).subscribe(
      (result: IPaginatedResult<ICompanyDetail>) => {
        this.loadingDrafts = false;
        this.isFiltering = false;

        // Ensure result has required properties with safe defaults
        const pageNumber = result?.pageNumber ?? 1;
        const pageSize = result?.pageSize ?? 25;
        const totalRecords = result?.totalRecords ?? 0;
        const data = result?.data ?? [];

        this.count_draftcompany = totalRecords;

        this.drafts_grid_data = { result: data, count: totalRecords };
      },
      error => {
        this.loadingDrafts = false;
        this.isFiltering = false;
        this.toastr.error(error.message || 'Failed to load drafts');
      }
    );
  }

  // Handle company grid data state changes (paging, sorting, searching, filtering)
  onCompanyDataStateChange(state: any): void {
    // Skip initial data load (handled by ngOnInit) - no action or refresh without search
    if (!state.action || (state.action.requestType === 'refresh' && !state.action.searchString)) {
      return;
    }
    this.updateRequestFromState(this.companyRequest, state);
    this.loadCompanies();
  }

  // Handle drafts grid data state changes (paging, sorting, searching, filtering)
  onDraftsDataStateChange(state: any): void {
    // Skip initial data load (handled by ngOnInit) - no action or refresh without search
    if (!state.action || (state.action.requestType === 'refresh' && !state.action.searchString)) {
      return;
    }
    this.updateRequestFromState(this.draftsRequest, state);
    this.loadDrafts();
  }

  // Update the pagination request based on Syncfusion grid state (paging, sorting, searching, filtering)
  private updateRequestFromState(request: IDashboardRequest, state: any): void {
    // Handle paging - use skip and take from dataStateChange
    if (state.skip !== undefined && state.take !== undefined) {
      request.pageNumber = Math.floor(state.skip / state.take) + 1;
      request.pageSize = state.take;
    }

    // Handle sorting
    if (state.sorted && state.sorted.length > 0) {
      const sortInfo = state.sorted[0];
      request.sortColumn = sortInfo.name;
      request.sortDirection = sortInfo.direction === 'descending' ? 'DESC' : 'ASC';
    } else if (state.action && state.action.requestType === 'sorting') {
      // Also check action for sorting info
      if (state.action.columnName) {
        request.sortColumn = state.action.columnName;
        request.sortDirection = state.action.direction === 'Descending' ? 'DESC' : 'ASC';
      } else {
        request.sortColumn = '';
        request.sortDirection = 'ASC';
      }
    }

    // Handle searching from dataStateChange
    if (state.search && state.search.length > 0) {
      request.searchTerm = state.search[0].key || '';
      request.pageNumber = 1; // Reset to first page on new search
    } else if (state.action && state.action.requestType === 'searching') {
      // Handle searching from action event
      request.searchTerm = state.action.searchString || '';
      request.pageNumber = 1; // Reset to first page on new search
    } else if (!state.search || state.search.length === 0) {
      // Clear search term when no search is active
      if (request.searchTerm) {
        request.searchTerm = '';
        request.pageNumber = 1;
      }
    }

    // Handle filtering from dataStateChange - rebuild filters from state.where
    if (state.action && (state.action.requestType === 'filtering' || state.action.requestType === 'clearfiltering')) {
      const newFilters: { [key: string]: string } = {};
      
      if (state.where && state.where.length > 0) {
        state.where.forEach(condition => {
          if (condition.predicates && condition.predicates.length > 0) {
            condition.predicates.forEach(pred => {
              if (pred.field && pred.value !== undefined && pred.value !== null && pred.value !== '') {
                this.addColumnFilter(newFilters, pred.field, pred.value, pred.operator);
              }
            });
          } else if (condition.field && condition.value !== undefined && condition.value !== null && condition.value !== '') {
            this.addColumnFilter(newFilters, condition.field, condition.value, condition.operator);
          }
        });
      }
      
      request.columnFilters = newFilters;
      request.pageNumber = 1;
    }
  }

  private addColumnFilter(filters: { [key: string]: string }, field: string, value: any, operator?: string): void {
    if (!field || value === undefined || value === null || value === '') {
      return;
    }

    const dateKeys = this.dateFilterKeys[field];
    if (dateKeys) {
      const dateValue = this.formatFilterDate(value);
      if (!dateValue) {
        return;
      }

      const normalizedOperator = (operator || 'equal').toLowerCase();
      if (normalizedOperator.indexOf('greater') >= 0) {
        filters[dateKeys.from] = dateValue;
      } else if (normalizedOperator.indexOf('less') >= 0) {
        filters[dateKeys.to] = dateValue;
      } else {
        filters[dateKeys.from] = dateValue;
        filters[dateKeys.to] = dateValue;
      }
      return;
    }

    if (field === 'version') {
      filters[field] = `${value}.0`;
    } else {
      filters[field] = String(value);
    }
  }

  private formatFilterDate(value: any): string {
    let date: Date;

    if (value instanceof Date) {
      date = value;
    } else {
      date = new Date(value);
    }

    if (isNaN(date.getTime())) {
      return '';
    }

    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }

  // Handle actionBegin - change filter operator from "startswith" to "contains"
  // and skip custom loading overlay for grid actions (Syncfusion shows its own spinner)
  public begin(args: any): void {
    // Skip custom loading overlay for paging, sorting, filtering (Syncfusion shows its own spinner)
    if (args.requestType === 'paging' || args.requestType === 'sorting' || args.requestType === 'filtering') {
      this.isFiltering = true;
    }

    if (args.requestType === 'filtering') {
      // Change operator from "startswith" to "contains" for text columns
      if (args.columns) {
        args.columns.forEach(col => {
          if (col.field !== 'modified') {
            col.operator = 'contains';
          }
        });
      }
      // Don't cancel - let dataStateChange handle the actual filtering
    }
  }

  // Display detail of a single company
  viewCompany(companyRevisionId: string, companyId: string) {
    if (companyRevisionId) {
      sessionStorage['currentCompanyRevisionId'] = companyRevisionId;

      this.router.navigate(['/company-details', companyRevisionId]);
    }
  }

    companyGridRowSelected(e: any){
        const id = e?.data?.companyRevisionId;
        this.selectedCompanyRevisionId = id;
        if (this.activeTab === 'drafts') {
          this.selectedCompanyRevisionIds.drafts = id;
        } else {
          this.selectedCompanyRevisionIds.company = id;
        }
    }

    companyGridRowDeselected() {
        this.selectedCompanyRevisionId = undefined;
        if (this.activeTab === 'drafts') {
          this.selectedCompanyRevisionIds.drafts = null;
        } else {
          this.selectedCompanyRevisionIds.company = null;
        }
    }

  /** Called by the grids after paging/sorting/filtering so we can reselect the row. */
  gridActionComplete(args: any) {
    switch (args?.requestType) {
      case 'paging':
      case 'sorting':
      case 'filtering':
      case 'searching':
      case 'refresh':
        this.restoreSelectionForActiveTab();
        break;
    }
  }

  private restoreSelectionForActiveTab() {
 const grid = this.activeTab === 'drafts' ? this.draftsGrid : this.company_grid;
 const id = this.activeTab === 'drafts' ? this.selectedCompanyRevisionIds.drafts : this.selectedCompanyRevisionIds.company;

 if (!grid || !id) {
 return;
 }

 const view = grid.getCurrentViewRecords() as any[];

 let rowIndex = -1;
 if (view && view.length) {
 for (let i =0; i < view.length; i++) {
 if (view[i] && view[i].companyRevisionId === id) {
 rowIndex = i;
 break;
 }
 }
 }

 if (rowIndex >=0) {
 grid.selectRow(rowIndex);
 }
}

  
}
